<script setup>
import { computed, onMounted, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useChips } from '@/composables/chips'
import { useCurrency } from '@/composables/currency'
import { useRateCard } from '@/composables/rate-card'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import GridDetailInfo from '@/components/shared/GridDetailInfo.vue'
import GridDetailTabs from '@/components/shared/GridDetailTabs.vue'
import StaffMovementsGrid from '@/components/planner/StaffMovementsGrid.vue'
import ExpenseClaimsGrid from '@/components/accounting/ExpenseClaimsGrid.vue'
import { useAccountingStore } from '@/stores/accounting.store'

const props = defineProps({
  staff: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { getServiceTagChips } = useChips()
const { getCurrencyString } = useCurrency()
const { priceLabel, costLabel } = useRateCard()

const { expenseClaimScopeEnum } = useAccountingStore()
const activeTab = ref('info')
const canSeeExpenses = props.staff.id === webSiteStore.staffId
  || webSiteStore.canApproveExpenseClaims
  || webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)
const stats = ref(null)

const amount = (value) => getCurrencyString(value, webSiteStore.getDefaultCurrency()?.code)
const isSet = (value) => value !== null && value !== undefined && value !== ''
const nameOf = (options, id) => options.find(option => option.id === id)?.name

const identityItems = computed(() => [
  { label: i18n.t('common.name'), value: [props.staff.first_name, props.staff.name].filter(Boolean).join(' ') },
  { label: i18n.t('common.code'), value: props.staff.code },
  { label: i18n.t('common.role'), value: nameOf(webSiteStore.getLocalizedRoles(), props.staff.role) },
  { label: i18n.t('common.email'), value: props.staff.email },
  { label: i18n.t('common.phone'), value: props.staff.phone },
  { label: i18n.t('common.availabilityType'), value: nameOf(portalStore.getLocalizedServiceAvailabilityTypes(), props.staff.availability_type) },
  { label: i18n.t('common.drivingLicense'), value: (props.staff.driving_licenses ?? []).map(id => nameOf(portalStore.getLocalizedDrivingLicenseTypes(), id)).join(', ') },
  { label: i18n.t('common.preferredLanguage'), value: portalStore.getLocalizedLanguages().find(language => language.code === props.staff.preferred_language)?.name },
  { label: i18n.t('common.price'), value: priceLabel(props.staff, true) },
  { label: i18n.t('common.cost'), value: costLabel(props.staff, true) },
].filter(item => isSet(item.value)))

const statsItems = computed(() => {
  if (!stats.value) return []
  const { project_count, completed_project_count, allocated_days, revenue, cost, margin, revenue_per_day, margin_pct } = stats.value
  return [
    { label: i18n.t('common.projectsCompleted'), value: `${completed_project_count} / ${project_count}` },
    { label: i18n.t('roiCompare.allocatedDays'), value: allocated_days },
    { label: i18n.t('common.revenue', 2), value: amount(revenue) },
    { label: i18n.t('common.cost'), value: amount(cost) },
    { label: i18n.t('common.margin'), value: amount(margin), trend: margin },
    { label: i18n.t('roiCompare.revenuePerDay'), value: isSet(revenue_per_day) ? amount(revenue_per_day) : null },
    { label: i18n.t('roiCompare.marginPct'), value: isSet(margin_pct) ? `${parseFloat(margin_pct).toFixed(2)}%` : null, trend: margin_pct },
  ].filter(item => isSet(item.value))
})

onMounted(async() => {
  if (!webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)) return
  const { data } = await planningStore.fetchRoiComparisonAsync(planningStore.roiComparisonTypeEnum.STAFF, [props.staff.id])
  stats.value = data[0] ?? null
})
</script>

<template>
  <grid-detail-tabs v-model="activeTab">
    <template #tabs>
      <q-tab name="info" :label="$t('common.info')" />
      <q-tab name="movements" :label="$t('menu.staff.movements')" />
      <q-tab v-if="canSeeExpenses" name="expenses" :label="$t('menu.accounting.expense_claims')" />
    </template>
    <template #panels>
      <q-tab-panel name="info" class="q-pa-none">
        <q-card-section horizontal>
          <q-avatar v-if="staff.avatar_file || staff.avatar_url" class="q-ma-md" size="10rem">
            <q-img :src="staff.avatar_file || staff.avatar_url" fit="cover" />
          </q-avatar>
          <q-icon
            v-else
            class="q-ma-md"
            size="10rem"
            color="grey-5"
            name="mdi-account-circle"
          />
          <q-separator vertical class="q-ma-md" />
          <grid-detail-info class="col q-py-md q-pr-md" :items="[...identityItems, ...statsItems]">
            <div v-if="staff.tags?.length" class="grid-detail-info__wide">
              <div class="text-caption text-grey-7">{{ $t('common.tag', 2) }}</div>
              <chip-template :values="getServiceTagChips(staff.tags)" />
            </div>
          </grid-detail-info>
        </q-card-section>
      </q-tab-panel>

      <q-tab-panel name="movements" class="q-pa-none">
        <staff-movements-grid :service="staff" />
      </q-tab-panel>

      <q-tab-panel v-if="canSeeExpenses" name="expenses" class="q-pa-none">
        <expense-claims-grid :scope="expenseClaimScopeEnum.STAFF" :staff="staff" />
      </q-tab-panel>
    </template>
  </grid-detail-tabs>
</template>
